import { apiClient } from './api';
import { Session, CreateSessionRequest, Position, ApiResponse } from '../types';

class SessionsService {
  private endpoint = '/admin/sessions';

  /**
   * Fetch all sessions
   */
  async getSessions(): Promise<Session[]> {
    try {
      const response = await apiClient.get<any>(this.endpoint);
      return response.data.sessions || [];
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }

  /**
   * Fetch a single session by ID
   */
  async getSession(sessionId: string): Promise<Session> {
    try {
      const response = await apiClient.get<any>(
        `${this.endpoint}/${sessionId}`
      );
      if (!response.data) {
        throw new Error('Session not found');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching session:', error);
      throw error;
    }
  }

  /**
   * Create a new session
   */
  async createSession(request: CreateSessionRequest): Promise<Session> {
    try {
      const response = await apiClient.post<any>(
        this.endpoint,
        request
      );
      if (!response.data) {
        throw new Error('Failed to create session');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  /**
   * Get positions for a session
   */
  async getSessionPositions(sessionId: string): Promise<Position[]> {
    try {
      const response = await apiClient.get<any>(
        `/admin/positions?sessionId=${sessionId}`
      );
      return response.data.positions || [];
    } catch (error) {
      console.error('Error fetching session positions:', error);
      throw error;
    }
  }

  /**
   * Get active sessions
   */
  async getActiveSessions(): Promise<Session[]> {
    try {
      const response = await apiClient.get<any>(
        `${this.endpoint}?status=ACTIVE`
      );
      return response.data.sessions || [];
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      throw error;
    }
  }

  /**
   * Get ended sessions
   */
  async getEndedSessions(): Promise<Session[]> {
    try {
      const response = await apiClient.get<any>(
        `${this.endpoint}?status=ENDED`
      );
      return response.data.sessions || [];
    } catch (error) {
      console.error('Error fetching ended sessions:', error);
      throw error;
    }
  }

  /**
   * End a session (admin only)
   */
  async endSession(sessionId: string): Promise<void> {
    try {
      await apiClient.post(`${this.endpoint}/${sessionId}/end`);
    } catch (error) {
      console.error('Error ending session:', error);
      throw error;
    }
  }
}

export const sessionsService = new SessionsService();
